// EmployeeAttendanceSummary.jsx - WITH SKELETON LOADING & SPECIFIC CLASSNAMES
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployeeSummary } from '../features/attendance/attendanceThunks';
import {
  selectEmployeeSummary,
  selectEmployeeSummaryLoading,
  selectEmployeeSummaryError,
  clearEmployeeSummary,
} from '../features/attendance/attendanceSlice';
import attendanceApi from '../api/attendanceApi';
import '../CSS/EmployeeAttendanceSummary.css';
import Sidebar from './Sidebar';

// Skeleton Components
const SkeletonInfoCard = () => (
  <div className="emp-summary-employee-info-card">
    <div className="emp-summary-info-header">
      <div className="emp-summary-skeleton-title"></div>
      <div className="emp-summary-skeleton-code"></div>
    </div>
    <div className="emp-summary-info-details">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="emp-summary-detail-item">
          <div className="emp-summary-skeleton-label"></div>
          <div className="emp-summary-skeleton-value"></div>
        </div>
      ))}
    </div>
  </div>
);

const SkeletonSummaryCard = () => (
  <div className="emp-summary-summary-card">
    <div className="emp-summary-card-content">
      <div className="emp-summary-skeleton-number"></div>
      <div className="emp-summary-skeleton-text"></div>
    </div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="emp-summary-skeleton-row">
    <td><div className="emp-summary-skeleton-cell"></div></td>
    <td><div className="emp-summary-skeleton-cell"></div></td>
    <td><div className="emp-summary-skeleton-cell"></div></td>
    <td><div className="emp-summary-skeleton-cell"></div></td>
    <td><div className="emp-summary-skeleton-cell"></div></td>
    <td><div className="emp-summary-skeleton-cell"></div></td>
  </tr>
);

const EmployeeAttendanceSummary = () => {
  const dispatch = useDispatch();
  const employeeSummary = useSelector(selectEmployeeSummary);
  const loading = useSelector(selectEmployeeSummaryLoading);
  const error = useSelector(selectEmployeeSummaryError);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch employees list on mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await attendanceApi.getAllEmployees();
        setEmployees(response.data || []);
      } catch (err) {
        console.error('Error fetching employees:', err);
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, []);

  // Fetch employee summary when employee is selected
  useEffect(() => {
    if (selectedEmployeeId) {
      dispatch(fetchEmployeeSummary({
        employeeId: selectedEmployeeId,
        month: selectedMonth,
        year: selectedYear,
      }));
    } else {
      dispatch(clearEmployeeSummary());
    }
  }, [dispatch, selectedEmployeeId, selectedMonth, selectedYear]);

  // Generate years array
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - 2 + i
  );

  // Get month name
  const getMonthName = (month) => {
    const date = new Date(2000, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long' });
  };

  // Format time
  const formatTime = (datetime) => {
    if (!datetime) return '--:--';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Get status class
  const getStatusClass = (status) => {
    const statusMap = {
      'PRESENT': 'emp-summary-status-present',
      'LATE': 'emp-summary-status-late',
      'ABSENT': 'emp-summary-status-absent',
      'HALF_DAY': 'emp-summary-status-halfday',
      'LEAVE': 'emp-summary-status-leave',
      'MEDICAL_LEAVE': 'emp-summary-status-medical',
    };
    return statusMap[status] || 'emp-summary-status-default';
  };

  // Filter employees by search term
  const filteredEmployees = employees.filter(emp => 
    emp.user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.id.toString().includes(searchTerm)
  );

  return (
    <div className='index-box'>
      <Sidebar />
      <div className="emp-summary-employee-attendance-summary">
        {/* Header */}
        <div className="emp-summary-summary-header">
          <h1>Employee Attendance Summary</h1>
          <p className="emp-summary-subtitle">View and manage employee attendance records</p>
        </div>

        {/* Employee Selection */}
        <div className="emp-summary-selection-panel">
          <div className="emp-summary-employee-selector">
            <label htmlFor="employee-search">Select Employee</label>
            <div className="emp-summary-employee-search-container">
              <input
                id="employee-search"
                type="text"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="emp-summary-employee-search"
              />
              <select
                value={selectedEmployeeId}
                onChange={(e) => {
                  setSelectedEmployeeId(e.target.value);
                  setSearchTerm('');
                }}
                className="emp-summary-employee-select"
                disabled={loadingEmployees}
              >
                <option value="">-- Select an employee --</option>
                {filteredEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.id} - {emp.user.first_name} {emp.user.last_name} ({emp.department || 'N/A'})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="emp-summary-date-filters">
            <div className="emp-summary-filter-group">
              <label htmlFor="month-select">Month</label>
              <select
                id="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="emp-summary-month-select"
                disabled={!selectedEmployeeId}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>
                    {getMonthName(month)}
                  </option>
                ))}
              </select>
            </div>

            <div className="emp-summary-filter-group">
              <label htmlFor="year-select">Year</label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="emp-summary-year-select"
                disabled={!selectedEmployeeId}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content Area */}
        {!selectedEmployeeId ? (
          <div className="emp-summary-empty-state">
            <div className="emp-summary-empty-icon">👤</div>
            <h2>No Employee Selected</h2>
            <p>Please select an employee from the dropdown above to view their attendance summary</p>
          </div>
        ) : loading ? (
          <>
            {/* Skeleton Loading */}
            <SkeletonInfoCard />
            
            <div className="emp-summary-summary-cards">
              {[...Array(6)].map((_, i) => <SkeletonSummaryCard key={i} />)}
            </div>

            <div className="emp-summary-records-section">
              <div className="emp-summary-section-header">
                <div className="emp-summary-skeleton-section-title"></div>
                <div className="emp-summary-skeleton-record-count"></div>
              </div>
              <div className="emp-summary-table-container">
                <table className="emp-summary-attendance-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Day</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Work Hours</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(5)].map((_, i) => <SkeletonTableRow key={i} />)}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : error ? (
          <div className="emp-summary-error-container">
            <p className="emp-summary-error-message">{error}</p>
            <button 
              onClick={() => dispatch(fetchEmployeeSummary({
                employeeId: selectedEmployeeId,
                month: selectedMonth,
                year: selectedYear,
              }))}
              className="emp-summary-btn-retry"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Employee Info Card */}
            <div className="emp-summary-employee-info-card">
              <div className="emp-summary-info-header">
                <h2>{employeeSummary.employeeName}</h2>
                <span className="emp-summary-employee-code">ID: {employeeSummary.employeeCode}</span>
              </div>
              <div className="emp-summary-info-details">
                <div className="emp-summary-detail-item">
                  <span className="emp-summary-label">Department:</span>
                  <span className="emp-summary-value">{employeeSummary.department || 'N/A'}</span>
                </div>
                <div className="emp-summary-detail-item">
                  <span className="emp-summary-label">Role:</span>
                  <span className="emp-summary-value">{employeeSummary.role || 'N/A'}</span>
                </div>
                <div className="emp-summary-detail-item">
                  <span className="emp-summary-label">Period:</span>
                  <span className="emp-summary-value">{getMonthName(selectedMonth)} {selectedYear}</span>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="emp-summary-summary-cards">
              <div className="emp-summary-summary-card emp-summary-card-present">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.present}</h3>
                  <p>Present</p>
                </div>
              </div>

              <div className="emp-summary-summary-card emp-summary-card-late">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.late}</h3>
                  <p>Late</p>
                </div>
              </div>

              <div className="emp-summary-summary-card emp-summary-card-absent">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.absent}</h3>
                  <p>Absent</p>
                </div>
              </div>

              <div className="emp-summary-summary-card emp-summary-card-halfday">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.halfDay}</h3>
                  <p>Half Day</p>
                </div>
              </div>

              <div className="emp-summary-summary-card emp-summary-card-leave">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.onLeave}</h3>
                  <p>On Leave</p>
                </div>
              </div>

              <div className="emp-summary-summary-card emp-summary-card-hours">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.avgWorkHours.toFixed(1)}</h3>
                  <p>Avg Hours/Day</p>
                </div>
              </div>
            </div>

            {/* Attendance Records Table */}
            <div className="emp-summary-records-section">
              <div className="emp-summary-section-header">
                <h3>Attendance Records</h3>
                <span className="emp-summary-record-count">{employeeSummary.count} records</span>
              </div>

              <div className="emp-summary-table-container">
                <table className="emp-summary-attendance-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Day</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Work Hours</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeSummary.records.length > 0 ? (
                      employeeSummary.records.map((record) => (
                        <tr key={record.id}>
                          <td>{new Date(record.date).toLocaleDateString()}</td>
                          <td>{new Date(record.date).toLocaleDateString('en-US', { weekday: 'short' })}</td>
                          <td>{formatTime(record.punch_in)}</td>
                          <td>{formatTime(record.punch_out)}</td>
                          <td>
                            {record.work_hours ? `${record.work_hours.toFixed(2)} hrs` : '--'}
                          </td>
                          <td>
                            <span className={`emp-summary-status-badge ${getStatusClass(record.status)}`}>
                              {record.status.replace('_', ' ')}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="emp-summary-no-data">
                          No attendance records for this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Statistics Footer */}
            <div className="emp-summary-stats-footer">
              <div className="emp-summary-stat-item">
                <span className="emp-summary-stat-label">Total Records:</span>
                <span className="emp-summary-stat-value">{employeeSummary.summary.totalRecords}</span>
              </div>
              <div className="emp-summary-stat-item">
                <span className="emp-summary-stat-label">Total Work Hours:</span>
                <span className="emp-summary-stat-value">{employeeSummary.summary.totalWorkHours.toFixed(2)} hrs</span>
              </div>
              <div className="emp-summary-stat-item">
                <span className="emp-summary-stat-label">Attendance Rate:</span>
                <span className="emp-summary-stat-value">
                  {employeeSummary.summary.totalRecords > 0
                    ? ((employeeSummary.summary.present / employeeSummary.summary.totalRecords) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeAttendanceSummary;
